<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center"
  >
    <div class="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-2xl font-semibold text-primary">
         {{  (dataType == 'Module/Staff' && role != 'admin') || role == 'admin' || dataType == 'Groups' || dataType == 'Users'  ? 'Excel Upload/Download' : 'Excel Download'}} for {{ mapBackendTypeToFrontend(dataType) }}
        </h3>
        <button
          @click="closeModal"
          class="text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <div class="mb-8" v-if="(dataType == 'Module/Staff' && role != 'admin') || role == 'admin' || dataType == 'Groups' || dataType == 'Users'">
        <h4 class="font-medium mb-2 text-lg">Upload Excel File</h4>
        <div class="flex items-center">
          <label
            for="file-upload"
            class="cursor-pointer bg-primary text-white px-4 py-2 rounded-l-md hover:bg-primary-light transition duration-300"
          >
            Choose File
          </label>
          <input
            id="file-upload"
            type="file"
            @change="handleFileUpload"
            accept=".xlsx, .xls"
            class="hidden"
          />
          <span
            class="border border-gray-300 rounded-r-md py-2 px-4 w-full truncate"
          >
            {{ selectedFile ? selectedFile.name : "No file chosen" }}
          </span>
        </div>
        <div
          class="flex items-center mt-2 mb-2"
          v-if="dataType != 'Module/Staff'"
        >
          <input
            type="checkbox"
            id="replace"
            v-model="replace"
            class="rounded border-gray-400 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
          <label for="replace" class="ml-2 text-sm text-gray-700"
            >Replace</label
          >
        </div>
        <button
          @click="uploadFile"
          :disabled="isUploading"
          class="mt-4 w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-light transition duration-300 flex items-center justify-center"
        >
          <template v-if="!isUploading">
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              ></path>
            </svg>
            Upload
          </template>
          <template v-else>
            <LoadingSpinner class="mr-2" />
            Uploading...
          </template>
        </button>
      </div>

      <div class="mb-8">
        <h4 class="font-medium mb-2 text-lg">Download Excel File</h4>
        <button
          @click="downloadFile"
          :disabled="isDownloading"
          class="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-light transition duration-300 flex items-center justify-center"
        >
          <template v-if="!isDownloading">
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              ></path>
            </svg>
            Download
          </template>
          <template v-else>
            <LoadingSpinner class="mr-2" />
            Downloading...
          </template>
        </button>
      </div>

      <div
        v-if="validationErrors.length > 0"
        class="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong class="font-bold">Validation Errors:</strong>
        <ul class="mt-2 list-disc list-inside">
          <li v-for="(error, index) in validationErrors" :key="index">
            {{ error }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
  
<script>
import { excelApi } from "../../api/excelManagement";
import { mapBackendTypeToFrontend } from "../../utils/dataTypeMapping";
import LoadingSpinner from "../LoadingSpinner.vue";
import { reportsApi } from "@/api/reports";

export default {
  name: "ExcelModal",
  components: {
    LoadingSpinner,
  },
  props: {
    show: Boolean,
    dataType: String,
    isNoAct: Boolean,
    role: String,
  },
  data() {
    return {
      selectedFile: null,
      validationErrors: [],
      isUploading: false,
      isDownloading: false,
      replace: false,
    };
  },
  methods: {
    handleFileUpload(event) {
      this.selectedFile = event.target.files[0];
      this.validationErrors = [];
    },
    async uploadFile() {
      if (!this.selectedFile) {
        alert("Please select a file to upload");
        return;
      }
      this.isUploading = true;
      try {
        await excelApi.uploadExcel(
          this.dataType,
          this.selectedFile,
          this.replace
        );
        alert("File uploaded successfully!");
        this.closeModal();
        this.$emit("refresh");
      } catch (error) {
        console.error("Error uploading file:", error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          this.validationErrors = error.response.data.errors;
        } else {
          alert(
            "An error occurred while uploading the file. Please try again."
          );
        }
      } finally {
        this.isUploading = false;
      }
    },
    async downloadFile() {
      this.isDownloading = true;
      try {
        let blob;
        if (this.dataType === "Module/Staff") {
          if (this.isNoAct)
          {
            blob = await reportsApi.exportModuleStaffNoActReport({
            searchQuery: this.searchQuery,
            school: this.selectedSchool,
            department: this.selectedDepartment,
            moduleCode: this.selectedModule
              ? this.selectedModule.moduleCode
              : "",
            isNoAct: this.isNoAct,
            });
          }
          else
            blob = await reportsApi.exportModuleStaffReport({
              searchQuery: this.searchQuery,
              school: this.selectedSchool,
              department: this.selectedDepartment,
              moduleCode: this.selectedModule
                ? this.selectedModule.moduleCode
                : "",
              isNoAct: this.isNoAct,
            });
        } else {
          blob = await excelApi.downloadExcel(this.dataType);
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${mapBackendTypeToFrontend(this.dataType)}.xlsx`
        );
        link.setAttribute("download", `${this.dataType}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        alert(
          "An error occurred while downloading the file. Please try again."
        );
      } finally {
        this.isDownloading = false;
      }
    },
    closeModal() {
      this.selectedFile = null;
      this.validationErrors = [];
      this.isUploading = false;
      this.isDownloading = false;
      this.$emit("close");
    },
    mapBackendTypeToFrontend,
  },
};
</script>
  
  